/** Kiểu dữ liệu & hằng số dùng chung client ↔ engine. Khớp với DTO server. */
export const APPEARANCE_OPTIONS = {
  hairStyle: [['buoi_cao', 'Búi cao'], ['xoa_dai', 'Xõa dài'], ['ngan', 'Tóc ngắn'], ['khan_vay', 'Khăn vấn']],
  hairColor: [['#1b1411', 'Đen'], ['#3d2a1e', 'Nâu sẫm'], ['#6b4a2f', 'Nâu đồng'], ['#9a9089', 'Bạc']],
  face: [['hien', 'Hiền'], ['nghiem', 'Nghiêm'], ['tre', 'Trẻ']],
  skinTone: [['#f1d2b4', 'Sáng'], ['#dcb08a', 'Trung'], ['#b9875f', 'Bánh mật'], ['#8a5d3d', 'Đậm']],
  outfit: [['ao_nau', 'Áo nâu'], ['ao_tram', 'Áo đỏ trầm'], ['ao_trang', 'Áo trắng ngà'], ['ao_lam', 'Áo lam']],
  accessory: [['khong', 'Không'], ['chuoi_hat', 'Chuỗi hạt'], ['tram_cai', 'Trâm cài'], ['den_nho', 'Đèn nhỏ']],
  aura: [['khong', 'Không'], ['dom_vang', 'Đốm vàng'], ['suong_lam', 'Sương lam']],
} as const;

export type AppearanceKey = keyof typeof APPEARANCE_OPTIONS;
export type Appearance = Record<AppearanceKey, string>;
export type Gender = 'nam' | 'nu' | 'khac';

export const OUTFIT_COLORS: Record<string, { robe: string; trim: string }> = {
  ao_nau: { robe: '#5a3a26', trim: '#b88a43' },
  ao_tram: { robe: '#6e231d', trim: '#e3be76' },
  ao_trang: { robe: '#d8ccb6', trim: '#7a251f' },
  ao_lam: { robe: '#2c3d4f', trim: '#b88a43' },
  // trang phục mở khóa (xem COSMETICS)
  ao_sen: { robe: '#e9c9cf', trim: '#c9708a' },
  ao_hoa_dang: { robe: '#8a1f16', trim: '#ffcf6a' },
  // chỉ dùng cho NPC
  giap_vang: { robe: '#c8901e', trim: '#8a1f16' },
  long_bao: { robe: '#e8b830', trim: '#c8281a' }, // hoàng bào Ngọc Hoàng (v0.14.1)
  ao_lan: { robe: '#b8261a', trim: '#ffd27a' },
  ca_sa_do: { robe: '#b8401a', trim: '#e8b33a' }, ca_sa_vang: { robe: '#c89a3a', trim: '#7a3a14' }, ca_sa_nau: { robe: '#6a4a2a', trim: '#c8a060' },
  ao_tu_than: { robe: '#7a4a2e', trim: '#d8402a' }, ao_xam: { robe: '#4a4a52', trim: '#8a7a5a' }, ao_beo: { robe: '#3a2e40', trim: '#b88a43' },
};

/**
 * Vật phẩm trang trí mở khóa theo tiến trình. Server giữ bản sao ở apps/server/src/features/catalog.ts
 * và là nơi quyết định đã mở hay chưa – client chỉ hiển thị.
 */
export type UnlockStat = 'collectibles' | 'levels' | 'streak' | 'lanterns' | 'achievements' | 'merit';
export interface CosmeticDef { key: string; slot: 'lamp' | 'outfit' | 'aura'; name: string; description: string; unlock: { stat: UnlockStat; min: number } | null }
export const COSMETICS: CosmeticDef[] = [
  { key: 'dau', slot: 'lamp', name: 'Đèn dầu', description: 'Ngọn đèn dầu ấm áp của Lão Đăng.', unlock: null },
  { key: 'long', slot: 'lamp', name: 'Đèn lồng giấy', description: 'Sáng hơn, ánh đỏ cam như đêm rằm.', unlock: { stat: 'collectibles', min: 3 } },
  { key: 'sen', slot: 'lamp', name: 'Đèn hoa sen', description: 'Ánh hồng dịu, soi rộng hơn.', unlock: { stat: 'levels', min: 3 } },
  { key: 'luu_ly', slot: 'lamp', name: 'Đèn lưu ly', description: 'Ánh lam trong vắt, soi xa nhất.', unlock: { stat: 'streak', min: 7 } },
  { key: 'ao_sen', slot: 'outfit', name: 'Áo hoa sen', description: 'Trang phục hồng nhạt viền sen.', unlock: { stat: 'streak', min: 3 } },
  { key: 'ao_hoa_dang', slot: 'outfit', name: 'Áo hoa đăng', description: 'Áo đỏ thắm viền vàng như đèn trôi sông.', unlock: { stat: 'lanterns', min: 3 } },
  { key: 'tinh_tu', slot: 'aura', name: 'Tinh tú', description: 'Những đốm sáng bạc xoay quanh người.', unlock: { stat: 'achievements', min: 5 } },
  { key: 'cong_duc', slot: 'aura', name: 'Hào quang công đức', description: 'Vầng sáng vàng sau đầu – của người tích nhiều công đức.', unlock: { stat: 'merit', min: 300 } },
];
export const UNLOCK_STAT_VI: Record<UnlockStat, string> = { collectibles: 'mảnh ký ức', levels: 'tầng hoàn thành', streak: 'ngày liên tiếp làm đủ việc mỗi ngày', lanterns: 'đèn hoa đăng đã thả', achievements: 'thành tựu', merit: 'điểm công đức' };
export type LampStyle = 'dau' | 'long' | 'sen' | 'luu_ly';

export const defaultAppearance = (): Appearance => ({ hairStyle: 'buoi_cao', hairColor: '#1b1411', face: 'hien', skinTone: '#dcb08a', outfit: 'ao_tram', accessory: 'chuoi_hat', aura: 'khong' });

export interface NpcInfo { id: number; key: string; name: string; title: string | null; modelKey: string; x: number; z: number; rotY: number }
export interface LocationInfo { key: string; kind: 'spawn' | 'exit' | 'interact' | 'poi'; label: string; x: number; z: number; radius: number; toLevel: number | null }
export interface CollectibleInfo { key: string; x: number; y: number; z: number }
export interface SceneData {
  level: { levelNo: number; name: string; theme: string; message: string; description: string; sceneKey: string; buildStatus: string };
  npcs: NpcInfo[]; locations: LocationInfo[]; collectibles: CollectibleInfo[];
}
export type Quality = 'low' | 'medium' | 'high';
/** v0.12: nhiều điệu nhảy ở sân khấu */
export const DANCES = [
  ['dance', 'Giơ tay'], ['dance_hiphop', 'Hip-hop nhún'], ['dance_disco', 'Disco chỉ trời'], ['dance_robot', 'Người máy'],
  ['dance_fan', 'Múa quạt'], ['dance_lion', 'Múa lân'], ['dance_spin', 'Xoay vòng'], ['dance_clap', 'Vỗ tay nhún nhảy'],
] as const;
export type DanceKey = (typeof DANCES)[number][0];
export type Anim = 'idle' | 'walk' | 'run' | 'talk' | 'bow' | 'point' | 'wave' | 'sit' | 'cheer' | DanceKey;
